import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '../../home/components/Navigation';
import { Footer } from '../../home/components/Footer';
import { ServiceModal } from '../../home/components/ServiceModal';
import { IndustriesModal } from '../../home/components/IndustriesModal';
import { HowItWorksModal } from '../../home/components/HowItWorksModal';
import { CalendarModal } from '../../home/components/CalendarModal';
import { SEO } from '../../../components/SEO';

const faqs = [
  {
    question: 'How does AI for painters help me get more painting jobs?',
    answer: 'When you\'re on a job site, you can\'t always stop to answer calls or respond to quote requests. Our AI answers every call instantly, collects project details, and books estimate appointments directly to your calendar — so you capture every lead while you\'re busy painting. Automation for painting companies means your pipeline fills itself.'
  },
  {
    question: 'Can the AI handle quote requests and estimate inquiries?',
    answer: 'Yes. Your AI is trained to collect the key information needed for a painting estimate — property type, square footage, interior vs. exterior, number of rooms, timeline, and contact details. You get a complete lead summary so you can show up to every estimate call fully prepared.'
  },
  {
    question: 'Will the AI follow up with leads who requested quotes but didn\'t book?',
    answer: 'Absolutely. Automated follow-up is one of the highest-ROI features for painting companies. When a homeowner requests a quote and doesn\'t respond, your AI sends a series of personalized follow-up texts and emails to re-engage them — dramatically increasing your close rate without any manual effort.'
  },
  {
    question: 'How does automated scheduling work for painting estimates?',
    answer: 'The AI checks your real-time availability and books estimate appointments directly into your calendar. It sends confirmation messages to the customer, reminder texts before the appointment, and follow-up messages after — keeping your schedule organized and reducing no-shows.'
  },
  {
    question: 'Does this work for both residential and commercial painting?',
    answer: 'Yes. Your AI can be configured to handle both residential and commercial inquiries differently — collecting the right information for each type of project and routing or scheduling accordingly. Whether it\'s a homeowner wanting a bedroom painted or a property manager needing a full building exterior, your AI handles it.'
  },
  {
    question: 'How quickly can I get set up?',
    answer: 'Most painting businesses are fully live within 48–72 hours. We handle the entire setup — training the AI on your services, service area, and pricing, connecting your calendar, and testing everything before launch. No technical knowledge required from you.'
  },
];

const painPoints = [
  {
    icon: 'ri-phone-line',
    title: 'Missing Calls While on Job Sites',
    description: 'You\'re on a ladder mid-stroke when your phone rings. By the time you can call back, the homeowner has already booked another painter. Every missed call is a job — and a paycheck — gone.'
  },
  {
    icon: 'ri-time-line',
    title: 'Slow Response to Quote Requests',
    description: 'Homeowners shopping for painters contact multiple companies at once. If you don\'t respond within minutes, you\'re already losing to whoever replied first. Slow follow-up is the #1 reason painting companies lose leads.'
  },
  {
    icon: 'ri-user-unfollow-line',
    title: 'Losing Leads to Faster Competitors',
    description: 'Automation for painting companies isn\'t just a nice-to-have anymore — it\'s a competitive necessity. If your competitors are responding instantly and you\'re calling back hours later, you\'re handing them your business.'
  },
  {
    icon: 'ri-calendar-close-line',
    title: 'Manual Scheduling Eating Your Time',
    description: 'Back-and-forth texts and calls to schedule estimates, confirm appointments, and send reminders adds up to hours every week. That\'s time you could spend on the job site — or with your family.'
  },
];

const solutions = [
  {
    icon: 'ri-phone-line',
    title: 'AI Phone Assistant — Answer Every Call Instantly',
    description: 'Your AI phone assistant answers every call in your company name, 24/7. It collects project details — interior or exterior, property type, square footage, timeline — and books estimate appointments directly to your calendar. No more missed calls, no more lost painting jobs.',
    link: '/ai-phone-answering-service',
    linkLabel: 'Learn about AI Phone Answering'
  },
  {
    icon: 'ri-chat-smile-3-line',
    title: 'AI Chatbot — Capture Online Leads Around the Clock',
    description: 'Most homeowners research painters online before calling. Your AI chatbot captures these website visitors 24/7 — answering questions about your services, collecting project details, and booking estimate appointments even while you sleep. Automation for painting companies starts with capturing every online lead.',
    link: '/services/ai-chatbot',
    linkLabel: 'Learn about AI Chatbot'
  },
  {
    icon: 'ri-calendar-check-line',
    title: 'Automated Scheduling — Fill Your Calendar Automatically',
    description: 'Customers book estimate appointments directly into your calendar based on your real availability. The system sends confirmation texts, appointment reminders, and post-estimate follow-ups automatically — keeping your schedule full and your no-show rate low.',
    link: '/ai-appointment-scheduling',
    linkLabel: 'Learn about Automated Scheduling'
  },
  {
    icon: 'ri-send-plane-line',
    title: 'Quote Follow-Up Automation — Close More Estimates',
    description: 'After every estimate, your AI sends a personalized follow-up sequence via text and email. If the customer goes quiet, it re-engages them automatically. This single feature alone can increase your close rate by 20–30% — without you making a single extra call.',
    link: '/services/ai-lead-follow-up',
    linkLabel: 'Learn about Follow-Up Automation'
  },
];

const benefits = [
  {
    icon: 'ri-calendar-2-line',
    stat: '40%',
    title: 'More Painting Jobs Booked',
    description: 'Capture every call and online inquiry — including evenings and weekends — and convert them into confirmed estimate appointments automatically.'
  },
  {
    icon: 'ri-flashlight-line',
    stat: '60 sec',
    title: 'Instant Response to Every Inquiry',
    description: 'Every lead gets a personalized response within 60 seconds. Be the first painter to respond and dramatically increase your chances of winning the job.'
  },
  {
    icon: 'ri-time-line',
    stat: '14 hrs',
    title: 'Admin Hours Saved Every Week',
    description: 'Stop spending hours on scheduling, follow-ups, and phone tag. AI handles the repetitive work so you can focus on painting and growing your business.'
  },
  {
    icon: 'ri-line-chart-line',
    stat: '30%',
    title: 'Higher Quote Conversion Rate',
    description: 'Automated follow-up sequences re-engage leads who went quiet after an estimate — turning more quotes into booked painting jobs without extra effort.'
  },
];

const steps = [
  {
    number: '01',
    title: 'We Set Up Your AI',
    description: 'We train your AI on your painting services, service area, residential and commercial offerings, and pricing. Setup takes 48–72 hours and we handle everything — no technical work required from you.'
  },
  {
    number: '02',
    title: 'AI Captures & Books',
    description: 'Your AI starts answering calls, responding to website inquiries, and booking estimate appointments directly to your calendar. Every lead is captured, responded to within 60 seconds, and followed up with automatically.'
  },
  {
    number: '03',
    title: 'You Show Up & Close',
    description: 'You arrive to confirmed estimate appointments with full project details already collected. No more phone tag, no more scheduling headaches — just more painting jobs on your calendar.'
  },
];

const useCases = [
  {
    icon: 'ri-home-4-line',
    title: 'Residential Interior & Exterior Quotes',
    description: 'A homeowner finds you online at 8pm and wants a quote for their exterior. Your AI chatbot captures their details, answers questions about your process and timeline, and books an estimate appointment for the following week — all without you being involved. You wake up to a confirmed appointment with full project details ready to go.',
    tag: 'Most common'
  },
  {
    icon: 'ri-building-2-line',
    title: 'Commercial Painting Inquiries',
    description: 'A property manager calls about painting a 12-unit apartment complex. Your AI collects the scope, timeline, and contact information, then schedules a site visit. Commercial leads often have longer decision cycles — your automated follow-up keeps you top of mind throughout the process without any manual effort.',
    tag: 'High value'
  },
  {
    icon: 'ri-repeat-2-line',
    title: 'Repeat Customer Re-Engagement',
    description: 'Past customers are your most valuable leads — they already trust you. Your AI automatically reaches out to customers who haven\'t booked in 12–18 months with a personalized message about seasonal painting specials or touch-up services. Automation for painting companies means your past customers keep coming back.',
    tag: 'Recurring revenue'
  },
];

export default function AIForPainters() {
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [selectedService, setSelectedService] = useState('');
  const [isIndustriesModalOpen, setIsIndustriesModalOpen] = useState(false);
  const [isHowItWorksModalOpen, setIsHowItWorksModalOpen] = useState(false);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleOpenService = (title: string) => {
    setSelectedService(title);
    setIsServiceModalOpen(true);
  };

  return (
    <>
      <SEO
        title="AI for Painters | Automation for Painting Companies | Dino Nuggets"
        description="AI for painters that answers every call, captures online leads, and books estimate appointments automatically. Stop losing painting jobs to faster competitors — automation for painting companies starts here."
        canonical="/industries/ai-for-painters"
        schemas={[
          {
            type: 'Service',
            data: {
              '@type': 'Service',
              name: 'AI Automation for Painting Businesses',
              description: 'AI-powered call answering, lead capture, automated scheduling, and quote follow-up for residential and commercial painting companies.',
              provider: { '@type': 'Organization', name: 'Dino Nuggets' },
              areaServed: 'United States',
              keywords: 'AI for painters, automation for painting companies, AI phone answering painters, automated scheduling painting business',
            },
          },
          {
            type: 'FAQPage',
            data: {
              '@type': 'FAQPage',
              mainEntity: faqs.map(f => ({
                '@type': 'Question',
                name: f.question,
                acceptedAnswer: { '@type': 'Answer', text: f.answer },
              })),
            },
          },
        ]}
      />

      <div className="min-h-screen bg-white">
        <Navigation />

        {/* Spring Sale Banner */}
        <div className="relative bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-white rounded-full translate-x-1/4 -translate-y-1/2"></div>
            <div className="absolute bottom-0 left-1/3 w-24 h-24 bg-white rounded-full translate-y-1/2"></div>
          </div>
          <div className="relative max-w-5xl mx-auto px-6 py-3.5 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-center sm:text-left">
            <div className="flex items-center gap-2.5">
              <span className="text-lg">🌱</span>
              <span className="font-bold text-base tracking-wide uppercase">Spring Sale</span>
              <span className="hidden sm:block w-px h-5 bg-white/40"></span>
            </div>
            <p className="text-sm sm:text-base font-medium text-white/95">
              <strong className="font-bold text-white">No setup fees</strong> on all automation packages through April 30th — save up to $500 on getting started.
            </p>
            <button
              onClick={() => setIsCalendarOpen(true)}
              className="flex-shrink-0 bg-white text-emerald-600 hover:bg-emerald-50 px-4 py-1.5 rounded-full text-sm font-bold transition-all whitespace-nowrap cursor-pointer"
            >
              Claim Offer
            </button>
          </div>
        </div>

        {/* Hero */}
        <section className="relative pt-32 pb-24 px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/readdy-assets/painters-hero-bg-001.jpg"
              alt="AI Automation for Painting Businesses"
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/50 to-black/70"></div>

          <div className="relative w-full max-w-4xl mx-auto text-center">
            <span className="inline-block bg-primary/20 border border-primary/40 text-primary px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Built for Residential &amp; Commercial Painters
            </span>
            <h1 className="font-display text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              AI Automation for Painting Businesses
            </h1>
            <p className="text-xl text-white/85 mb-10 leading-relaxed max-w-3xl mx-auto">
              Capture more leads and book more painting jobs without answering every call. AI for painters handles your phones, online inquiries, scheduling, and quote follow-up — automatically, 24/7.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Book a Free Consultation
              </button>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white/10 hover:bg-white/20 border border-white/30 text-white px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                See How It Works
              </button>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="bg-secondary py-12 px-6 lg:px-8">
          <div className="max-w-5xl mx-auto grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '24/7', label: 'Call answering — never miss a painting lead again' },
              { value: '60 sec', label: 'Response time to every new quote request' },
              { value: '40%', label: 'More estimate appointments booked on average' },
              { value: '48 hrs', label: 'From sign-up to fully automated' },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm leading-snug">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Main Content */}
        <section className="py-20 px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">

            {/* Intro */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                Why Painting Companies Are Turning to AI
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                The painting business is more competitive than ever. Homeowners and property managers have more options, they research online before calling, and they hire whoever responds first. If you\'re still relying on voicemail and manual follow-up, you\'re losing jobs every single week to competitors who are faster.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                AI for painters changes the equation entirely. Your <Link to="/services/ai-receptionist" className="text-primary hover:underline font-medium">AI receptionist</Link> answers every call, responds to every online inquiry within 60 seconds, books estimate appointments automatically, and follows up with every lead who hasn\'t converted yet — all without you lifting a finger. Automation for painting companies isn\'t about replacing the human touch; it\'s about making sure no lead ever slips through the cracks.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Whether you run a solo operation or manage a crew of painters, the result is the same: more booked jobs, less admin work, and a business that keeps growing even when you\'re on the job site.
              </p>
            </div>

            {/* Pain Points */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                The Problems Costing Painters Jobs Every Week
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Sound familiar? These are the four biggest reasons painting companies lose revenue — and exactly what AI for painters is built to solve. Explore how <Link to="/industries/ai-for-home-services" className="text-primary hover:underline font-medium">home service businesses</Link> use the same automation to stay ahead.
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {painPoints.map((point) => (
                  <div key={point.title} className="bg-gray-50 p-6 rounded-xl flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-red-50 rounded-lg">
                      <i className={`${point.icon} text-2xl text-red-400`}></i>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">{point.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{point.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Real Cost */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-10 rounded-2xl">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-6">
                What Slow Response Is Really Costing Your Painting Business
              </h2>
              <p className="text-gray-700 leading-relaxed mb-8">
                Painting is a high-consideration purchase — homeowners get multiple quotes and hire based on trust and responsiveness. The painter who responds first, follows up consistently, and makes the process easy wins the job. AI for painters ensures that painter is always you.
              </p>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { stat: '$1,500–$8,000+', color: 'text-red-400', label: 'Average residential painting job value', note: 'Each missed call or slow follow-up could mean thousands of dollars going to a competitor.' },
                  { stat: '78%', color: 'text-amber-400', label: 'Homeowners hire the first contractor who responds', note: 'Speed of response is the single biggest factor in winning painting estimates.' },
                  { stat: '3–5x', color: 'text-primary', label: 'More Google reviews with automated requests', note: 'Automated review requests after every completed job build your reputation on autopilot.' },
                ].map((item) => (
                  <div key={item.stat} className="bg-white p-6 rounded-xl border border-gray-200">
                    <div className={`text-3xl font-bold ${item.color} mb-2`}>{item.stat}</div>
                    <div className="text-sm font-semibold text-gray-900 mb-2">{item.label}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{item.note}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Solution */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                How Dino Nuggets AI Works for Painting Companies
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Our complete automation for painting companies covers every stage of your lead-to-job pipeline — from the first call to the final follow-up:
              </p>
              <div className="space-y-6">
                {solutions.map((solution) => (
                  <div key={solution.title} className="bg-gray-50 p-6 rounded-xl flex gap-5">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg">
                      <i className={`${solution.icon} text-2xl text-primary`}></i>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-xl text-gray-900 mb-2">{solution.title}</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">{solution.description}</p>
                      <Link
                        to={solution.link}
                        className="text-primary hover:underline text-sm font-medium inline-flex items-center gap-1 cursor-pointer"
                      >
                        {solution.linkLabel}
                        <i className="ri-arrow-right-line text-xs"></i>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Benefits */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                What AI for Painters Delivers
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Real, measurable results that show up in your calendar and your revenue:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {benefits.map((benefit) => (
                  <div key={benefit.title} className="border border-gray-200 rounded-xl p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg flex-shrink-0">
                        <i className={`${benefit.icon} text-2xl text-primary`}></i>
                      </div>
                      <div className="text-3xl font-bold text-primary">{benefit.stat}</div>
                    </div>
                    <h3 className="font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* How It Works */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                How It Works: 3 Simple Steps
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Getting started with AI for painters is easier than you think. No technical knowledge required — we handle everything.
              </p>
              <div className="space-y-6">
                {steps.map((step) => (
                  <div key={step.number} className="flex gap-6 items-start">
                    <div className="flex-shrink-0 w-14 h-14 flex items-center justify-center bg-primary/10 rounded-xl">
                      <span className="text-primary font-bold text-lg">{step.number}</span>
                    </div>
                    <div className="flex-1 pt-1">
                      <h3 className="font-semibold text-gray-900 text-lg mb-1">{step.title}</h3>
                      <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Use Cases */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-4">
                Real Scenarios Where AI for Painters Wins You Jobs
              </h2>
              <p className="text-gray-600 mb-10 leading-relaxed">
                Here\'s exactly how automation for painting companies handles the situations your business deals with every day:
              </p>
              <div className="space-y-6">
                {useCases.map((useCase) => (
                  <div key={useCase.title} className="border border-gray-200 rounded-xl p-6 hover:border-primary/40 transition-colors">
                    <div className="flex items-start gap-5">
                      <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center bg-primary/10 rounded-lg">
                        <i className={`${useCase.icon} text-2xl text-primary`}></i>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="font-semibold text-xl text-gray-900">{useCase.title}</h3>
                          <span className="bg-primary/10 text-primary text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">{useCase.tag}</span>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{useCase.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Internal Links */}
            <div className="mb-20 bg-gradient-to-br from-gray-50 to-primary/5 p-8 rounded-xl">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                Core Services Behind Your Painting Business Automation
              </h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Explore the individual services that power AI for painters:
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <Link
                  to="/ai-phone-answering-service"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">AI Phone Answering Service</h3>
                    <p className="text-sm text-gray-600">Professional AI that answers every call in your company name, 24/7 — capturing leads and booking estimate appointments automatically.</p>
                  </div>
                </Link>
                <Link
                  to="/ai-appointment-scheduling"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-check-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">AI Appointment Scheduling</h3>
                    <p className="text-sm text-gray-600">Automatic calendar booking for estimate appointments — with confirmations, reminders, and follow-ups sent to every customer.</p>
                  </div>
                </Link>
                <Link
                  to="/services/ai-chatbot"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-chat-smile-3-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">AI Chatbot</h3>
                    <p className="text-sm text-gray-600">Capture and qualify website leads 24/7 — answer questions about your painting services and book estimate appointments automatically.</p>
                  </div>
                </Link>
                <Link
                  to="/services/ai-lead-follow-up"
                  className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary hover:shadow-md transition-all cursor-pointer group flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-send-plane-line text-2xl text-primary"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary transition-colors mb-1">AI Lead Follow-Up</h3>
                    <p className="text-sm text-gray-600">Respond to every new lead in 60 seconds and follow up on every quote automatically — turning more estimates into booked jobs.</p>
                  </div>
                </Link>
              </div>
            </div>

            {/* Other Industries */}
            <div className="mb-20">
              <h2 className="font-display text-2xl font-bold text-gray-900 mb-4">
                See How We Help Other Trades &amp; Home Service Businesses
              </h2>
              <div className="flex flex-wrap gap-3">
                <Link to="/ai-for-contractors" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Contractors</Link>
                <Link to="/ai-for-roofers" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Roofers</Link>
                <Link to="/ai-for-landscapers" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Landscapers</Link>
                <Link to="/industries/ai-for-appliance-repair" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Appliance Repair</Link>
                <Link to="/industries/ai-for-home-services" className="bg-gray-100 hover:bg-primary/10 hover:text-primary text-gray-700 px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap">AI for Home Services</Link>
              </div>
            </div>

            {/* FAQ */}
            <div className="mb-20">
              <h2 className="font-display text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors cursor-pointer"
                    >
                      <h3 className="font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      <i className={`ri-arrow-down-s-line text-2xl text-gray-400 flex-shrink-0 transition-transform ${openFaqIndex === index ? 'rotate-180' : ''}`}></i>
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Final CTA */}
            <div className="bg-gradient-to-br from-primary to-secondary text-white p-12 rounded-2xl text-center">
              <h2 className="font-display text-3xl font-bold mb-4">
                Turn Missed Calls Into Booked Jobs
              </h2>
              <p className="text-lg mb-8 text-white/90 max-w-2xl mx-auto">
                Every unanswered call and slow follow-up is a painting job going to your competitor. AI for painters ensures you\'re always first to respond — capturing every lead, booking every estimate, and following up on every quote. Get set up in 48 hours.
              </p>
              <button
                onClick={() => setIsCalendarOpen(true)}
                className="bg-white text-primary hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
              >
                Get Started Today
              </button>
            </div>

          </div>
        </section>

        <Footer
          onOpenService={handleOpenService}
          onOpenIndustries={() => setIsIndustriesModalOpen(true)}
          onOpenHowItWorks={() => setIsHowItWorksModalOpen(true)}
        />
        <ServiceModal isOpen={isServiceModalOpen} onClose={() => setIsServiceModalOpen(false)} title={selectedService} />
        <IndustriesModal isOpen={isIndustriesModalOpen} onClose={() => setIsIndustriesModalOpen(false)} />
        <HowItWorksModal isOpen={isHowItWorksModalOpen} onClose={() => setIsHowItWorksModalOpen(false)} />
        <CalendarModal isOpen={isCalendarOpen} onClose={() => setIsCalendarOpen(false)} />
      </div>
    </>
  );
}
